import * as React from "react";
import { Box, Tooltip } from "@mui/material";
import { fetchMyGroupMembers } from "../../api/queries";
import type { EditedBy } from "../../types/payload";

// Who wrote what on the group's sheet (see supabase/sql/sheet_authors.sql):
// every sheet row carries an edited_by map (field -> profile id of the
// member who last changed it). This resolves those ids to the emails of
// the group's members, so hovering an entry someone else wrote says who.

type SheetAuthors = {
  myId: string | null;
  emailById: Map<string, string>;
  refresh: () => void;
};

const SheetAuthorsContext = React.createContext<SheetAuthors>({ myId: null, emailById: new Map(), refresh: () => {} });

const REFRESH_INTERVAL_MS = 10000;

export function SheetAuthorsProvider(props: { groupId: string; children: React.ReactNode }) {
  const [members, setMembers] = React.useState<{ myId: string | null; emailById: Map<string, string> }>({
    myId: null,
    emailById: new Map(),
  });
  const lastLoad = React.useRef(0);

  const load = React.useCallback(() => {
    lastLoad.current = Date.now();
    fetchMyGroupMembers().then(({ data }) => {
      if (!data) return;
      setMembers({
        myId: data.find((m) => m.is_me)?.profile_id ?? null,
        emailById: new Map(data.map((m) => [m.profile_id, m.email])),
      });
    });
  }, []);

  React.useEffect(() => {
    load();
  }, [props.groupId, load]);

  // Someone may have joined the group since the list was loaded; an unknown
  // author asks for a reload, at most once every few seconds.
  const refresh = React.useCallback(() => {
    if (Date.now() - lastLoad.current > REFRESH_INTERVAL_MS) load();
  }, [load]);

  const value = React.useMemo(() => ({ ...members, refresh }), [members, refresh]);

  return <SheetAuthorsContext.Provider value={value}>{props.children}</SheetAuthorsContext.Provider>;
}

// Wraps one editable field of a sheet row. Hovering it shows who last edited
// that field -- unless it was you, or nobody has touched it yet.
export function AuthorTip(props: { editedBy: EditedBy | null | undefined; field: string; children: React.ReactNode }) {
  const { myId, emailById, refresh } = React.useContext(SheetAuthorsContext);

  const authorId = props.editedBy?.[props.field];
  const bySomeoneElse = Boolean(myId && authorId && authorId !== myId);
  const email = bySomeoneElse && authorId ? emailById.get(authorId) : undefined;

  return (
    <Tooltip
      arrow
      placement="top"
      // Hover only: a tooltip popping up over a field while typing in it gets in the way.
      disableFocusListener
      title={bySomeoneElse ? `Last edited by ${email ?? "a former group member"}` : ""}
      onOpen={() => {
        if (bySomeoneElse && !email) refresh();
      }}
    >
      {/* grid: the wrapped field keeps stretching to the full width, as it did unwrapped */}
      <Box sx={{ display: "grid", minWidth: 0 }}>{props.children}</Box>
    </Tooltip>
  );
}
